import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import { ToastStatusEnum } from '@components/ui/toaster';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { usePostSkyJackerCommandMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';

import {
  SkyJackerCommand,
  SkyJackerCommandEnum,
  SkyJackerSensorTrackingCommand,
  SkyJackerSetEmissionCommand,
  Target,
} from '@/types/c2/c2.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { SensorTypeEnum, SkyJackerSensorConfiguration } from '@/types/sensor/sensors';

import { useAppSelector } from './redux.hooks';

type UseSkyJackerCommandParameter =
  | MonitoringSensorByType<SensorTypeEnum.SKY_JACKER>
  | SensorOrSubSensorInfoByType<SensorTypeEnum.SKY_JACKER>
  | null;

export type UseSkyJackerCommandFunctions = {
  sensorTracking: (target: Target<TargetLocation> | null, configuration?: SkyJackerSensorConfiguration) => void;
  setEmission: (emitting: boolean, configuration?: SkyJackerSensorConfiguration) => void;
  isLoading: boolean;
  isError: boolean;
};

export const useSkyJackerCommand = (monitoringSensor: Readonly<UseSkyJackerCommandParameter>) => {
  const [postSkyJackerCommand, { isError, isLoading }] = usePostSkyJackerCommandMutation();

  const user = useAppSelector(selectActiveUser);
  const sensorConfiguration = monitoringSensor?.configuration ?? null;
  const site = user.activeSite?.code ?? '';
  const appCode = sensorConfiguration?.appCode ?? '';
  const code = sensorConfiguration?.code ?? '';
  const name = sensorConfiguration?.name ?? '';

  const getCommand = (command: SkyJackerCommand) => {
    switch (command.type) {
      case SkyJackerCommandEnum.SkyJackerSetEmission: {
        return { command: 'sensors.commands.skyjacker.emissionCommand', commandValues: { state: command.emitting } };
      }
      case SkyJackerCommandEnum.SkyJackerSensorTracking: {
        return {
          command: 'sensors.commands.skyjacker.trackingCommand',
          commandValues: { state: command.trackedTargetId !== null },
        };
      }
      case SkyJackerCommandEnum.SkyJackerGeoDesignation: {
        return { command: 'sensors.commands.skyjacker.geoDesignationCommand' };
      }
    }
  };

  async function sendCommand(command: SkyJackerCommand, configuration?: SkyJackerSensorConfiguration) {
    const fullCommand = {
      site: configuration?.site ?? site,
      appCode: configuration?.appCode ?? appCode,
      command,
    };

    const toastParams = {
      ...getCommand(command),
      name: configuration?.name ?? name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    return postSkyJackerCommand(fullCommand)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  }

  const sensorTracking = (target: Target<TargetLocation> | null, configuration?: SkyJackerSensorConfiguration) => {
    sendCommand(
      {
        type: SkyJackerCommandEnum.SkyJackerSensorTracking,
        sensorCode: configuration?.code ?? code,
        trackedTargetId: target?.id ?? null,
      } as SkyJackerSensorTrackingCommand,
      configuration,
    );
  };

  const setEmission = (emitting: boolean, configuration?: SkyJackerSensorConfiguration) => {
    sendCommand(
      {
        type: SkyJackerCommandEnum.SkyJackerSetEmission,
        sensorCode: configuration?.code ?? code,
        emitting: emitting,
      } as SkyJackerSetEmissionCommand,
      configuration,
    );
  };

  return {
    sensorTracking,
    setEmission,
    isLoading,
    isError,
  } as UseSkyJackerCommandFunctions;
};
